import React, {useCallback, useEffect, useMemo, useState} from "react"
import dayjs from "dayjs"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import PageSection from "@/layouts/PageSection"
import {
   deleteRecorridosProperties,
   getRecorridosProperties,
   postRecorridosProperties,
   putRecorridosProperties,
   RecorridoFormData,
} from "@/api/properties"

type RecorridosSectionProps = {
   propertyId: string
   canManageRecorridos: boolean
}

type RecorridoRow = RecorridoFormData & {
   id: string
}

type ScrollIndicators = {
   showTop: boolean
   showBottom: boolean
   hasOverflow: boolean
}

const initialFormState: RecorridoFormData = {
   asesor: "",
   cliente: "",
   fecha: "",
   comentarios: "",
}

const parseDateForInput = (value: unknown): string => {
   if (!value) return ""
   if (typeof value === "string") {
      const parsed = dayjs(value)
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : ""
   }
   if (value instanceof Date) {
      return dayjs(value).format("YYYY-MM-DD")
   }
   const timestamp = value as {seconds?: number; _seconds?: number; toDate?: () => Date}
   const seconds = timestamp.seconds ?? timestamp._seconds
   if (typeof seconds === "number") {
      return dayjs.unix(seconds).format("YYYY-MM-DD")
   }
   if (typeof timestamp.toDate === "function") {
      return dayjs(timestamp.toDate()).format("YYYY-MM-DD")
   }
   return ""
}

const formatDisplayDate = (value: string): string => {
   if (!value) return "Sin fecha"
   const parsed = dayjs(value)
   return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value
}

const extractRecorridosPayload = (value: unknown): unknown[] => {
   if (!value) return []
   if (Array.isArray(value)) return value
   if (typeof value !== "object") return []
   const payload = value as Record<string, unknown>
   if (Array.isArray(payload.recorridos)) {
      return payload.recorridos
   }
   if (Array.isArray(payload.data)) {
      return payload.data
   }
   const nested = payload.data as Record<string, unknown> | undefined
   if (nested && Array.isArray(nested.recorridos)) {
      return nested.recorridos
   }
   return []
}

const normalizeRecorridoEntry = (entry: unknown, index: number): RecorridoRow => {
   const normalized = (entry as Record<string, unknown>) || {}
   const asesorValue =
      normalized.asesor ?? normalized.responsable ?? normalized.advisor ?? normalized.partner ?? ""
   const cliente =
      normalized.cliente ??
      normalized.client ??
      normalized.customer ??
      normalized.clientePrincipal ??
      ""
   const fecha = parseDateForInput(
      normalized.fecha ?? normalized.date ?? normalized.fechaRecorrido ?? normalized.recorridoDate,
   )
   const comentarios =
      normalized.comentarios ??
      normalized.comments ??
      normalized.note ??
      normalized.observaciones ??
      ""
   const numero =
      normalized.numero ??
      normalized.number ??
      normalized.no ??
      normalized.recorridoNumber ??
      normalized.recorridoNo ??
      index + 1
   const id = String(
      normalized.id ??
         normalized._id ??
         normalized.recorridoId ??
         `${cliente}-${asesorValue}-${numero}`,
   )
   return {
      id,
      asesor: String(asesorValue ?? ""),
      cliente: String(cliente ?? ""),
      fecha,
      comentarios: String(comentarios ?? ""),
      numero: typeof numero === "number" ? numero : Number(numero) || index + 1,
   }
}

const RecorridosSection = ({propertyId, canManageRecorridos}: RecorridosSectionProps) => {
   const [recorridos, setRecorridos] = useState<RecorridoRow[]>([])
   const [formState, setFormState] = useState<RecorridoFormData>(initialFormState)
   const [editingId, setEditingId] = useState<string | null>(null)
   const [loading, setLoading] = useState(false)
   const [saving, setSaving] = useState(false)
   const [error, setError] = useState("")

   const fetchRecorridos = useCallback(async () => {
      if (!propertyId) {
         setRecorridos([])
         return
      }
      setLoading(true)
      try {
         const response = await getRecorridosProperties(propertyId)
         const payload = extractRecorridosPayload(response)
         const normalized = payload.map((entry, index) => normalizeRecorridoEntry(entry, index))
         setRecorridos(normalized)
      } catch (fetchError) {
         console.error("Error cargando recorridos", fetchError)
      } finally {
         setLoading(false)
      }
   }, [propertyId])

   useEffect(() => {
      fetchRecorridos()
   }, [fetchRecorridos])

   const handleInputChange = (field: keyof RecorridoFormData, value: string) => {
      setFormState((prev) => ({...prev, [field]: value}))
   }

   const resetForm = () => {
      setFormState(initialFormState)
      setEditingId(null)
      setError("")
   }

   const handleSave = async () => {
      if (!canManageRecorridos || saving) return
      if (!formState.asesor.trim() || !formState.cliente.trim() || !formState.fecha) {
         setError("Completa los campos requeridos.")
         return
      }
      setSaving(true)
      setError("")
      try {
         if (editingId) {
            await putRecorridosProperties(propertyId, editingId, formState)
         } else {
            await postRecorridosProperties(propertyId, formState)
         }
         resetForm()
         await fetchRecorridos()
      } catch (saveError) {
         console.error("Error guardando recorrido", saveError)
         setError("No se pudo guardar el recorrido.")
      } finally {
         setSaving(false)
      }
   }

   const handleEdit = (recorrido: RecorridoRow) => {
      if (!canManageRecorridos) return
      setEditingId(recorrido.id)
      setFormState({
         asesor: recorrido.asesor,
         cliente: recorrido.cliente,
         fecha: recorrido.fecha,
         comentarios: recorrido.comentarios,
         numero: recorrido.numero,
      })
      setError("")
   }

   const handleDelete = async (recorridoId: string) => {
      if (!canManageRecorridos || saving) return
      setSaving(true)
      setError("")
      try {
         await deleteRecorridosProperties(propertyId, recorridoId)
         await fetchRecorridos()
         if (editingId === recorridoId) {
            resetForm()
         }
      } catch (deleteError) {
         console.error("Error eliminando recorrido", deleteError)
         setError("No se pudo eliminar el recorrido.")
      } finally {
         setSaving(false)
      }
   }

   const displayedRecorridos = useMemo(() => {
      const sorted = [...recorridos]
      return sorted.sort((a, b) => {
         const dateA = dayjs(a.fecha).isValid() ? dayjs(a.fecha).unix() : 0
         const dateB = dayjs(b.fecha).isValid() ? dayjs(b.fecha).unix() : 0
         return dateB - dateA
      })
   }, [recorridos])

   const columnsCount = canManageRecorridos ? 6 : 5

   const CommentCell = ({text}: {text?: string}) => {
      const [indicators, setIndicators] = useState<ScrollIndicators>({
         showTop: false,
         showBottom: false,
         hasOverflow: false,
      })
      const containerRef = React.useRef<HTMLDivElement | null>(null)

      const updateIndicators = useCallback(() => {
         const element = containerRef.current
         if (!element) return
         const {scrollTop, scrollHeight, clientHeight} = element
         const hasOverflow = scrollHeight > clientHeight + 1
         const showTop = hasOverflow && scrollTop > 0
         const showBottom = hasOverflow && scrollTop + clientHeight < scrollHeight - 1
         setIndicators((prev) =>
            prev.showTop === showTop &&
            prev.showBottom === showBottom &&
            prev.hasOverflow === hasOverflow
               ? prev
               : {showTop, showBottom, hasOverflow},
         )
      }, [])

      useEffect(() => {
         updateIndicators()
      }, [text, updateIndicators])

      return (
         <div className="relative">
            <div
               className={`max-h-24 overflow-y-auto pr-2 md:max-h-none md:overflow-visible md:pr-0 ${
                  indicators.hasOverflow
                     ? "rounded border border-gray-300 p-2 md:border-0 md:p-0"
                     : ""
               }`}
               onScroll={updateIndicators}
               ref={containerRef}
            >
               {text || "-"}
            </div>
            {indicators.showTop && (
               <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-gray-500/40 to-transparent md:hidden" />
            )}
            {indicators.showBottom && (
               <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-gray-500/40 to-transparent md:hidden" />
            )}
         </div>
      )
   }

   return (
      <PageSection id="property-recorridos" title="Recorridos" className="space-y-4">
         {canManageRecorridos && (
            <div className="space-y-3">
               <div className="grid gap-2 md:grid-cols-3">
                  <input
                     className="w-full rounded border border-gray-300 px-3 py-2"
                     placeholder="Responsable"
                     value={formState.asesor}
                     onChange={(e) => handleInputChange("asesor", e.target.value)}
                  />
                  <input
                     className="w-full rounded border border-gray-300 px-3 py-2"
                     placeholder="Cliente"
                     value={formState.cliente}
                     onChange={(e) => handleInputChange("cliente", e.target.value)}
                  />
                  <div className="relative w-full md:hidden">
                     <input
                        type="text"
                        className="w-full rounded border border-gray-300 px-3 py-2 pr-10"
                        placeholder="dd/mm/aaaa"
                        value={formState.fecha ? dayjs(formState.fecha).format("DD/MM/YYYY") : ""}
                        readOnly
                        aria-label="Fecha de recorrido"
                     />
                     <input
                        type="date"
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        value={formState.fecha}
                        onChange={(e) => handleInputChange("fecha", e.target.value)}
                        aria-label="Seleccionar fecha de recorrido"
                     />
                     <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-600">
                        <CalendarMonthIcon fontSize="small" />
                     </span>
                  </div>
                  <div className="relative hidden w-full md:block">
                     <input
                        type="date"
                        className="date-input-no-native-icon w-full rounded border border-gray-300 px-3 py-2 pr-10 text-gray-400"
                        placeholder="dd/mm/aaaa"
                        value={formState.fecha}
                        onChange={(e) => handleInputChange("fecha", e.target.value)}
                     />
                     <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-600">
                        <CalendarMonthIcon fontSize="small" />
                     </span>
                  </div>
               </div>
               <textarea
                  className="w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="Comentarios"
                  rows={2}
                  value={formState.comentarios}
                  onChange={(e) => handleInputChange("comentarios", e.target.value)}
               />
               <div className="flex flex-wrap items-center justify-end gap-2">
                  {editingId && (
                     <button
                        type="button"
                        className="rounded border border-gray-400 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                        onClick={resetForm}
                        disabled={saving}
                     >
                        Cancelar
                     </button>
                  )}
                  <button
                     type="button"
                     className="rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-green-300"
                     onClick={handleSave}
                     disabled={saving}
                  >
                     {editingId ? "Guardar recorrido" : "Agregar recorrido +"}
                  </button>
               </div>
               {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
         )}
         <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
               <thead className="bg-gray-100 text-left text-xs uppercase text-gray-500">
                  <tr>
                     <th className="py-2 px-3 text-center">No. Recorrido</th>
                     <th className="py-2 px-3">Asesor</th>
                     <th className="py-2 px-3">Cliente</th>
                     <th className="py-2 px-3 text-center">Fecha de recorrido</th>
                     <th className="py-2 px-3 min-w-[220px] sm:min-w-[260px] md:min-w-0">
                        Comentarios
                     </th>
                     {canManageRecorridos && <th className="py-2 px-3 text-center">Acciones</th>}
                  </tr>
               </thead>
               <tbody>
                  {loading ? (
                     <tr>
                        <td colSpan={columnsCount} className="py-4 text-center text-gray-500">
                           Cargando recorridos...
                        </td>
                     </tr>
                  ) : displayedRecorridos.length === 0 ? (
                     <tr>
                        <td colSpan={columnsCount} className="py-4 text-center text-gray-500">
                           No hay recorridos registrados
                        </td>
                     </tr>
                  ) : (
                     displayedRecorridos.map((recorrido, index) => (
                        <tr key={recorrido.id} className="border-b border-gray-200 last:border-0">
                           <td className="py-3 px-3 text-center font-semibold">
                              {displayedRecorridos.length - index}
                           </td>
                           <td className="py-3 px-3">{recorrido.asesor || "-"}</td>
                           <td className="py-3 px-3">{recorrido.cliente || "-"}</td>
                           <td className="py-3 px-3 text-center">
                              {formatDisplayDate(recorrido.fecha)}
                           </td>
                           <td className="py-3 px-3 align-top min-w-[240px] border-black sm:min-w-[260px] md:min-w-0">
                              <CommentCell text={recorrido.comentarios || ""} />
                           </td>
                           {canManageRecorridos && (
                              <td className="py-3 px-3">
                                 <div className="flex items-center justify-center gap-2">
                                    <button
                                       type="button"
                                       className="text-green-600 disabled:text-green-300"
                                       onClick={() => handleEdit(recorrido)}
                                       disabled={saving}
                                       aria-label="Editar recorrido"
                                    >
                                       <EditIcon fontSize="small" />
                                    </button>
                                    <button
                                       type="button"
                                       className="text-red-600 disabled:text-red-300"
                                       onClick={() => handleDelete(recorrido.id)}
                                       disabled={saving}
                                       aria-label="Eliminar recorrido"
                                    >
                                       <DeleteIcon fontSize="small" />
                                    </button>
                                 </div>
                              </td>
                           )}
                        </tr>
                     ))
                  )}
               </tbody>
            </table>
         </div>
      </PageSection>
   )
}

export default RecorridosSection
